<script setup lang="ts">
import { ref, watchEffect, watch, nextTick } from 'vue';

type Option = {
	label: string;
	value: string | number;
} | null;

const emit = defineEmits(['select', 'clearSearchTerm', 'inputSearchTerm']);

const props = defineProps<{
	placeholder?: string;
	label?: string;
	options: Option[];
	selectedValue?: string | number;
	disabled?: boolean;
	hasError?: boolean;
	errorLabel?: string;
	ariaLabelOpen?: string;
	ariaLabelClose?: string;
	dropdownPositionTop?: boolean;
	hasSearch?: boolean;
}>();

const isOpen = ref(false);
const selectedOption = ref<Option>(null);
const arrowCounter = ref(-1);
const optionRefs = ref([]);
const searchTerm = ref('');
const searchAutocompleteInput = ref(); /** Focus input when clicking clear btn */

watchEffect(() => {
	if (props.options?.length && props.selectedValue) {
		selectedOption.value = props.options.find(
			(option) => option?.value === props.selectedValue
		);
	} else {
		selectedOption.value = null;
	}
});

const closeDropdown = () => {
	isOpen.value = false;
	arrowCounter.value = -1; /** Keyboard navigation */
};

function handleSelect(option: Option) {
	/** placeholders and noMatch options are passed
	 * with an empty string as value */
	if (option?.value) {
		selectedOption.value = option;
		searchTerm.value = option.label;
		emit('select', option.value);
		if (props.hasSearch) emit('inputSearchTerm', option.label);
	}

	closeDropdown();
}

const handleClear = () => {
	isOpen.value = true;
	searchTerm.value = '';
	searchAutocompleteInput.value.focus();
	emit('clearSearchTerm');
};

const isItemSelected = (option): boolean => option.value === selectedOption.value?.value;

const scrollInView = () => {
	const activeElement = optionRefs.value.find(
		(optionRef) => optionRefs.value.indexOf(optionRef) === arrowCounter.value
	);

	if (activeElement) {
		activeElement?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
	}
};

const handleArrowDown = () => {
	if (arrowCounter.value + 1 < props.options.length) {
		arrowCounter.value += 1;
	}
	scrollInView();
};

const handleArrowUp = () => {
	if (arrowCounter.value > 0) {
		arrowCounter.value -= 1;
	}
	scrollInView();
};

const handleKeydownEnter = () => {
	if (isOpen.value && arrowCounter.value !== -1) {
		handleSelect(props.options[arrowCounter.value]);
		closeDropdown();
	} else {
		isOpen.value = !(isOpen.value && arrowCounter.value === -1);
	}
};

const handleInput = (e) => {
	if (!isOpen.value) isOpen.value = true;

	if (!e.target.value) {
		emit('clearSearchTerm');
	} else {
		emit('inputSearchTerm', e.target.value);
	}
};

const clickOutside = () => {
	closeDropdown();

	if (props.hasSearch && selectedOption.value) {
		searchTerm.value = `${selectedOption.value?.label}`;
	}
};

watch(isOpen, async () => {
	await nextTick();
	if (isOpen.value && selectedOption.value?.label) {
		const selectedElement = optionRefs.value.find((optionRef) => {
			return optionRef.innerText === `${selectedOption.value?.label}`;
		});
		selectedElement.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		});
	}
});
</script>

<template>
	<div
		:class="[
			'select',
			{
				'select--disabled': disabled,
			},
		]"
		data-ts="select-box"
	>
		<span v-if="label" class="select__label" data-ts="select-label">
			{{ label }}
		</span>
		<div
			v-click-outside="clickOutside"
			:aria-label="isOpen ? ariaLabelClose : ariaLabelOpen"
			aria-haspopup="listbox"
			tabindex="0"
			:class="['select__display-wrapper', { 'select__display-wrapper--error': hasError }]"
			data-ts="select"
			@click="isOpen = !isOpen"
			@keydown.enter.prevent="handleKeydownEnter"
			@keydown.down.prevent="handleArrowDown"
			@keydown.up.prevent="handleArrowUp"
			@keydown.esc="isOpen = false"
		>
			<template v-if="hasSearch">
				<label>
					<span class="select__search-icon bx bx-search" />
					<input
						v-model="searchTerm"
						ref="searchAutocompleteInput"
						data-ts="search-autocomplete-input"
						:placeholder="placeholder"
						type="text"
						class="select__input"
						@input="handleInput"
					/>
				</label>
				<button
					v-show="searchTerm"
					type="button"
					tabindex="0"
					data-ts="autocomplete-clear-btn"
					aria-label="Clear input"
					class="select__clear-btn bx bx-x"
					@click.stop="handleClear"
					@keydown.enter.stop="handleClear"
				/>
			</template>
			<div
				v-else
				:class="[
					'select__selected',
					{
						select__selected__placeholder: !selectedOption,
					},
				]"
			>
				<span>{{ selectedOption ? selectedOption?.label : placeholder }}</span>
				<span
					:class="[
						'select__selected__arrow bx bx-chevron-down',
						{ 'select__selected__arrow--open': isOpen },
					]"
				/>
			</div>
			<ul
				v-if="isOpen"
				id="options"
				:class="[
					'select__items',
					'popover-container',
					{ 'select__items--top': dropdownPositionTop },
				]"
				role="list"
				:aria-expanded="`${isOpen}`"
			>
				<li
					v-for="(option, i) in options"
					:key="`select-option-${i}`"
					ref="optionRefs"
					:data-ts="`select-option-${i}`"
					:class="[
						'select__items__item',
						{ 'select__items__item--disabled': !option.value },
						{ 'select__items__item--active': i === arrowCounter && option.value },
						{ 'select__items__item--selected': isItemSelected(option) },
					]"
					role="option"
					@click.stop="handleSelect(option)"
				>
					<slot name="label" :option="option">
						{{ option.label }}
					</slot>
				</li>
			</ul>
		</div>
		<p v-if="hasError" class="select__error" data-ts="error-label">
			{{ errorLabel }}
		</p>
	</div>
</template>

<style lang="scss" scoped>
$dropdown-max-height: 200px;

.select {
	$this: &;
	display: flex;
	flex-direction: column;
	position: relative;
	font-size: $font-size;

	&__display-wrapper {
		border-radius: $border-radius-2;
		border: $border-width-1 $border-style $color-gray-200;
		height: $height-form-element;

		&--error {
			border-color: $color-error-700;
		}

		@media (hover: hover) {
			&:hover {
				border: $border-width-1 $border-style $color-input-hover;
			}
		}

		&:focus-visible:not([disabled]) {
			outline: $border-width-2 $border-style $color-focus;
		}
	}

	&--disabled {
		pointer-events: none;
		.select__display-wrapper {
			border: $border-width-1 $border-style $color-gray-100;
		}
		#{$this}__selected {
			color: $color-gray-800 !important;
			background-color: $color-gray-100 !important;
		}
	}

	&__label {
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		margin-bottom: $spacing-1;
	}

	&__selected {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		border-radius: $border-radius-2;
		padding: 0 $spacing-8 0 $spacing-2;
		text-align: left;
		background-color: $color-white;
		cursor: pointer;
		white-space: normal;
		overflow: hidden;

		&__arrow {
			position: absolute;
			right: $spacing-2;
			font-size: $font-size-md;
			transition: transform 200ms ease-out;

			&--open {
				transform: rotate(180deg);
				transition: transform 200ms ease-out;
			}
		}

		&__placeholder {
			color: $color-neutral-400;
		}
	}

	&__items {
		width: 100%;
		height: auto;
		max-height: $dropdown-max-height;
		position: absolute;
		top: 100%;
		border-radius: $border-radius-2;
		border: $border-width-1 $border-style $color-neutral-200;
		overflow-x: hidden;
		background-color: $color-white;
		overscroll-behavior: contain;
		box-shadow: $box-shadow-dropdown;
		transform-origin: top center;
		z-index: $z-index-2;
		margin-top: $spacing-1;

		&--top {
			top: auto;
			bottom: 100%;
			margin-top: 0;
			margin-bottom: $spacing-1;
		}

		&--reversed {
			top: auto;
			bottom: 100%;
			margin: 0 0 $spacing-1 0;
		}

		&__item {
			min-height: calc(#{$dropdown-max-height} / 5);
			padding: $spacing-2;
			user-select: none;
			list-style: none;
			display: flex;
			align-items: center;
			white-space: normal;
			cursor: pointer;

			&--active,
			&:hover,
			&:focus-visible {
				background-color: $color-black;
				color: $color-white;
				outline: none;
				border: none;
			}

			@media (hover: hover) {
				&:hover {
					background-color: $color-neutral-100;
					color: black;
				}
			}

			&--selected {
				font-weight: $font-weight-bold !important;
			}

			&--disabled {
				pointer-events: none;
			}
		}
	}

	&__error {
		position: absolute;
		top: 100%;
		padding-left: $spacing-1;
		margin-top: $spacing-1;
		color: $color-error-700;
		font-size: $font-size-sm;
	}

	&__search-icon {
		position: absolute;
		bottom: calc(#{$height-form-element} / 2);
		left: $spacing-2;
		transform: translateY(55%);
	}

	&__input {
		width: 100%;
		height: 100%;
		border: none;
		border-radius: $border-radius-2;
		padding: $spacing-2 $spacing-8 $spacing-2 $spacing-6;
	}

	&__clear-btn {
		position: absolute;
		right: 0;
		bottom: calc(#{$height-form-element} / 2);
		transform: translateY(50%);
		font-size: $font-size-md;
		padding: $spacing-2;
		border-radius: $border-radius-2;

		@media (hover: hover) {
			&:hover,
			&:focus-visible {
				color: $color-error-700;
			}
		}
	}
}
</style>
